import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        marginBottom: 8,
        borderRadius: 10,
        backgroundColor: '#f7f8ff',
        border: '1px solid #e6e8f7',
    },
    group: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 4,
        padding: '2px 4px 2px 8px',
        borderRadius: 8,
        backgroundColor: '#fff',
        border: '1px solid #e0e2f0',
    },
    groupLabel: {
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.6px',
        textTransform: 'uppercase',
        color: '#8a92a6',
        marginRight: 2,
        whiteSpace: 'nowrap',
    },
    valueChip: {
        maxWidth: 220,
        height: 24,
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 700,
        color: '#3A38A0',
        backgroundColor: '#eef0ff',
        border: '1px solid #c9cdf2',
        '& .MuiChip-deleteIcon': {
            color: '#7c81c4',
            width: 15,
            height: 15,
            '&:hover': { color: '#3A38A0' },
        },
    },
    moreChip: {
        height: 24,
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 800,
        color: '#43419e',
        backgroundColor: '#e2e5ff',
        border: '1px solid #c9cdf2',
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#d5d9ff' },
    },
    clearGroupButton: {
        padding: 3,
        color: '#a0a6bd',
        '&:hover': { color: '#d94a4a', backgroundColor: 'transparent' },
    },
    clearAllButton: {
        marginLeft: 'auto',
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 13,
        borderRadius: 8,
        padding: '4px 10px',
        color: '#515f6d',
        whiteSpace: 'nowrap',
        '&:hover': { backgroundColor: '#eceef8' },
    },
    summary: {
        fontSize: 12,
        fontWeight: 700,
        color: '#8a92a6',
        whiteSpace: 'nowrap',
    },
}));

interface FilterOptionLike {
    name: string;
    label: string;
    [key: string]: any;
}

interface FilteredStateV2Props {
    /** Same shape the filter pickers consume: `{ name, label }` per filter. */
    filterOptions?: FilterOptionLike[];
    /** `{ [filterName]: [{ label, value }] }` — the applied selection. */
    filteredValues?: Record<string, any>;
    /** Remove one value from one filter. */
    onRemoveValue: (name: string, option: any) => void;
    /** Remove every value of one filter. */
    onRemoveGroup: (name: string) => void;
    /** Omitted → the "Clear all" action is hidden. */
    onClearAll?: () => void;
    /** Values shown per filter before the rest collapse behind "+N". */
    inlineLimit?: number;
    /** Filter keys that should never render a chip. */
    excludeKeys?: string[];
}

/**
 * Applied-filter summary. Unlike the original FilteredState — which joined a
 * filter's values into one comma-separated chip that could only be removed as a
 * whole — every selected value gets its own chip and its own delete, so a
 * six-vendor filter can be narrowed one vendor at a time.
 */
const FilteredStateV2: React.FC<FilteredStateV2Props> = ({
    filterOptions = [],
    filteredValues = {},
    onRemoveValue,
    onRemoveGroup,
    onClearAll,
    inlineLimit = 3,
    excludeKeys = ['startDate', 'endDate'],
}) => {
    const styles = useStyles();
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const groups = useMemo(() => {
        return (filterOptions ?? [])
            .filter((option) => !excludeKeys.includes(option?.name))
            .map((option) => {
                const raw = filteredValues?.[option?.name];
                // Tolerate a bare string/number as well as the usual
                // `{ label, value }[]` the pickers hand back.
                const values = Array.isArray(raw)
                    ? raw
                    : raw === null || raw === undefined || raw === ''
                      ? []
                      : [{ label: String(raw), value: raw }];
                return { ...option, values };
            })
            .filter((group) => group.values.length > 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterOptions, filteredValues]);

    if (!groups.length) return null;

    const totalCount = groups.reduce(
        (sum, group) => sum + group.values.length,
        0,
    );

    return (
        <Box className={styles.root}>
            <Typography className={styles.summary}>
                {`${totalCount} filter${totalCount === 1 ? '' : 's'} applied`}
            </Typography>

            {groups.map((group) => {
                const isExpanded = !!expanded[group.name];
                const visible = isExpanded
                    ? group.values
                    : group.values.slice(0, inlineLimit);
                const hiddenCount = group.values.length - visible.length;

                return (
                    <Box key={group.name} className={styles.group}>
                        <Typography className={styles.groupLabel}>
                            {group.label}
                        </Typography>

                        {visible.map((item: any, index: number) => (
                            <Chip
                                key={`${item?.value ?? item?.label}-${index}`}
                                size="small"
                                className={styles.valueChip}
                                label={item?.label ?? String(item?.value ?? '')}
                                title={item?.label}
                                onDelete={() => onRemoveValue(group.name, item)}
                            />
                        ))}

                        {hiddenCount > 0 && (
                            <Chip
                                size="small"
                                className={styles.moreChip}
                                label={`+${hiddenCount}`}
                                onClick={() =>
                                    setExpanded((prev) => ({
                                        ...prev,
                                        [group.name]: true,
                                    }))
                                }
                            />
                        )}
                        {isExpanded && group.values.length > inlineLimit && (
                            <Chip
                                size="small"
                                className={styles.moreChip}
                                label="Show less"
                                onClick={() =>
                                    setExpanded((prev) => ({
                                        ...prev,
                                        [group.name]: false,
                                    }))
                                }
                            />
                        )}

                        <Tooltip title={`Clear ${group.label}`} arrow>
                            <IconButton
                                size="small"
                                className={styles.clearGroupButton}
                                aria-label={`Clear ${group.label} filter`}
                                onClick={() => onRemoveGroup(group.name)}
                            >
                                <CloseIcon style={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            })}

            {!!onClearAll && (
                <Button className={styles.clearAllButton} onClick={onClearAll}>
                    Clear all
                </Button>
            )}
        </Box>
    );
};

export default FilteredStateV2;
